import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    FolderKanban,
    Search,
    Filter,
    Users,
    Calendar,
    Target,
    Clock,
    CheckCircle,
    AlertCircle,
    Eye,
    ChevronRight,
    BarChart3,
    Star,
    MessageSquare,
    Bookmark,
    Share2,
    Download,
    MoreVertical
} from "lucide-react";
import BackButton from "../../components/BackButton";
import projectImg from "../../imgs/projects.svg";

const ViewProjects = () => {
    const [projects, setProjects] = useState([
        {
            id: 1,
            name: "Website Redesign",
            description: "Complete overhaul of company website with modern design",
            status: "active",
            progress: 85,
            deadline: "Dec 15, 2024",
            team: "Design Team",
            role: "UI Designer",
            tasks: { assigned: 8, completed: 6 },
            priority: "high",
            lastActivity: "2 hours ago"
        },
        {
            id: 2,
            name: "Mobile App V2",
            description: "Development of next-generation mobile application",
            status: "active",
            progress: 60,
            deadline: "Jan 10, 2025",
            team: "Dev Team",
            role: "Frontend Developer",
            tasks: { assigned: 12, completed: 7 },
            priority: "medium",
            lastActivity: "1 day ago"
        },
        {
            id: 3,
            name: "API Integration",
            description: "Third-party API integration for payment processing",
            status: "active",
            progress: 95,
            deadline: "Nov 30, 2024",
            team: "Backend Team",
            role: "API Specialist",
            tasks: { assigned: 4, completed: 4 },
            priority: "high",
            lastActivity: "3 hours ago"
        },
        {
            id: 4,
            name: "Analytics Dashboard",
            description: "Real-time analytics and reporting dashboard",
            status: "planning",
            progress: 45,
            deadline: "Dec 20, 2024",
            team: "Data Team",
            role: "Data Analyst",
            tasks: { assigned: 6, completed: 3 },
            priority: "medium",
            lastActivity: "2 days ago"
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [selectedTeam, setSelectedTeam] = useState("all");
    const [bookmarked, setBookmarked] = useState([1, 3]);

    const statusOptions = [
        { value: "all", label: "All Statuses" },
        { value: "active", label: "Active", color: "bg-blue-100 text-blue-800" },
        { value: "planning", label: "Planning", color: "bg-gray-100 text-gray-800" },
        { value: "completed", label: "Completed", color: "bg-green-100 text-green-800" },
        { value: "on-hold", label: "On Hold", color: "bg-yellow-100 text-yellow-800" }
    ];

    const teams = ["All Teams", "Design Team", "Dev Team", "Backend Team", "Data Team"];

    const getStatusColor = (status) => {
        switch (status) {
            case "active": return "bg-blue-100 text-blue-800";
            case "planning": return "bg-gray-100 text-gray-800";
            case "completed": return "bg-green-100 text-green-800";
            case "on-hold": return "bg-yellow-100 text-yellow-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "high": return "bg-red-100 text-red-800";
            case "medium": return "bg-yellow-100 text-yellow-800";
            case "low": return "bg-green-100 text-green-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const toggleBookmark = (id) => {
        setBookmarked(prev =>
            prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
        );
    };

    const filteredProjects = projects.filter(project => {
        const matchesSearch =
            project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            project.team.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = selectedStatus === "all" || project.status === selectedStatus;
        const matchesTeam = selectedTeam === "all" || project.team === selectedTeam;

        return matchesSearch && matchesStatus && matchesTeam;
    });

    const stats = {
        assigned: projects.reduce((sum, p) => sum + p.tasks.assigned, 0),
        completed: projects.reduce((sum, p) => sum + p.tasks.completed, 0),
        avgProgress: Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length),
        deadlines: projects.filter(p => p.status === "active").length
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/team-member/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">My Projects</h1>
                                <p className="text-gray-600 mt-1">View and manage your assigned projects</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button className="flex items-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Stats Overview */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Assigned Tasks</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.assigned}</p>
                                </div>
                                <Target className="w-8 h-8 text-primary-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Completed Tasks</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
                                </div>
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Avg. Progress</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.avgProgress}%</p>
                                </div>
                                <BarChart3 className="w-8 h-8 text-blue-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Active Projects</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.deadlines}</p>
                                </div>
                                <Clock className="w-8 h-8 text-yellow-600" />
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filters */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search projects..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                        <div className="flex gap-3">
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedTeam}
                                onChange={(e) => setSelectedTeam(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {teams.map(team => (
                                    <option key={team} value={team}>
                                        {team}
                                    </option>
                                ))}
                            </select>
                            <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                                <Filter className="w-5 h-5 mr-2" />
                                More Filters
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Projects List */}
                    <div className="lg:col-span-2">
                        <div className="space-y-4">
                            {filteredProjects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="card hover:shadow-medium group"
                                >
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-start space-x-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mt-1">
                                                        <FolderKanban className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center space-x-3 mb-1">
                                                            <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                                                            <div className="flex items-center space-x-2">
                                <span className={`badge ${getStatusColor(project.status)}`}>
                                  {project.status}
                                </span>
                                                                <span className={`badge ${getPriorityColor(project.priority)}`}>
                                  {project.priority}
                                </span>
                                                            </div>
                                                        </div>
                                                        <p className="text-gray-600 text-sm mb-3">{project.description}</p>

                                                        <div className="flex flex-wrap gap-4 text-sm mb-3">
                                                            <div className="flex items-center space-x-2">
                                                                <Users className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{project.team}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                <Star className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{project.role}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{project.deadline}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => toggleBookmark(project.id)}
                                                        className={`p-2 rounded-lg transition-colors ${
                                                            bookmarked.includes(project.id)
                                                                ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100'
                                                                : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50'
                                                        }`}
                                                        title={bookmarked.includes(project.id) ? "Remove bookmark" : "Bookmark project"}
                                                    >
                                                        <Bookmark className="w-4 h-4" fill={bookmarked.includes(project.id) ? "currentColor" : "none"} />
                                                    </button>
                                                    <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Progress and Tasks */}
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center space-x-4">
                                                        <div className="text-sm">
                                                            <span className="text-gray-600">Tasks: </span>
                                                            <span className="font-medium text-gray-900">
                                {project.tasks.completed}/{project.tasks.assigned}
                              </span>
                                                        </div>
                                                        <div className="text-sm">
                                                            <span className="text-gray-600">Last activity: </span>
                                                            <span className="font-medium text-gray-900">{project.lastActivity}</span>
                                                        </div>
                                                    </div>
                                                    <button className="flex items-center text-primary-600 hover:text-primary-700 font-medium">
                                                        <Eye className="w-4 h-4 mr-1" />
                                                        View Details
                                                    </button>
                                                </div>

                                                <div className="space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">Overall Progress</span>
                                                        <span className="font-medium text-gray-900">{project.progress}%</span>
                                                    </div>
                                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${
                                                                project.progress < 50 ? 'bg-red-500' :
                                                                    project.progress < 80 ? 'bg-yellow-500' : 'bg-green-500'
                                                            }`}
                                                            style={{ width: `${project.progress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Empty State */}
                        {filteredProjects.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-12"
                            >
                                <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <FolderKanban className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
                                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                    {searchTerm || selectedStatus !== 'all' || selectedTeam !== 'all'
                                        ? "No projects match your search criteria. Try adjusting your filters."
                                        : "You are not assigned to any projects yet."
                                    }
                                </p>
                                {(searchTerm || selectedStatus !== 'all' || selectedTeam !== 'all') && (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedStatus('all');
                                            setSelectedTeam('all');
                                        }}
                                        className="btn-primary"
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </motion.div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Project Overview */}
                        <div className="card">
                            <div className="text-center mb-6">
                                <img
                                    src={projectImg}
                                    alt="Projects Overview"
                                    className="w-full max-w-xs mx-auto mb-4 rounded-lg"
                                />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Overview</h3>
                                <p className="text-gray-600 text-sm">
                                    Track your progress, collaborate with team members, and stay on top of deadlines.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <span className="font-medium text-gray-700">Task Completion</span>
                                    </div>
                                    <span className="font-medium text-gray-900">
                    {Math.round((stats.completed / stats.assigned) * 100)}%
                  </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Clock className="w-5 h-5 text-yellow-600" />
                                        <span className="font-medium text-gray-700">Upcoming Deadlines</span>
                                    </div>
                                    <span className="font-medium text-gray-900">2 projects</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <AlertCircle className="w-5 h-5 text-blue-600" />
                                        <span className="font-medium text-gray-700">Team Messages</span>
                                    </div>
                                    <span className="font-medium text-gray-900">5 unread</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                            <div className="space-y-2">
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <MessageSquare className="w-5 h-5" />
                                        <span className="font-medium">Team Chat</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Share2 className="w-5 h-5" />
                                        <span className="font-medium">Share Updates</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Download className="w-5 h-5" />
                                        <span className="font-medium">Download Reports</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                            <div className="space-y-3">
                                {projects.slice(0, 3).map(project => (
                                    <div key={project.id} className="flex items-start space-x-3">
                                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                            <FolderKanban className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{project.name}</p>
                                            <p className="text-xs text-gray-500">{project.lastActivity}</p>
                                        </div>
                                        <div className="text-right">
                                            <div className={`w-2 h-2 rounded-full ${
                                                project.progress < 50 ? 'bg-red-500' :
                                                    project.progress < 80 ? 'bg-yellow-500' : 'bg-green-500'
                                            }`} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProjects;